import type { BoxProps } from '@hover/blueprint';
import {
  Box,
  Button,
  Center,
  Icon,
  useBoolean,
  useBreakpointValue,
} from '@hover/blueprint';
import { iPlus } from '@hover/icons';
import type { PanInfo } from 'framer-motion';
import { animate, motion, useMotionValue } from 'framer-motion';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useHomeownerAppContext } from 'src/modules/homeownerApp/hooks';
import { HomeownerAppMenuNames } from 'src/modules/homeownerApp/providers/HomeownerAppProvider';

import { CarouselArrow } from './CarouselArrow';
import { CarouselElement } from './CarouselElement';
import { InfinitePager } from './InfinitePager';
import { SlidePager } from './SlidePager';

const range = [-1, 0, 1];

interface InfiniteCarouselProps extends BoxProps {
  /**
   * The render prop for the carousel items
   */
  children: (props: { index: number }) => JSX.Element;
  /**
   * Whether the carousel should auto play, default is false
   */
  autoPlay?: boolean;
  /**
   * Number of elements in the carousel
   */
  numberOfElements: number;
  /**
   * Whether the carousel should loop infinitely, default is false
   */
  loop?: boolean;
  /**
   * Color for controls background, default is 'darkAlpha.700'
   */
  controlsBackground?: string;
}

const MotionBox = motion(Box);

const InfiniteCarousel = ({
  autoPlay = false,
  children,
  numberOfElements,
  loop = false,
  controlsBackground = 'darkAlpha.700',
  ...rest
}: InfiniteCarouselProps) => {
  const { setCurrentMenu } = useHomeownerAppContext();
  const x = useMotionValue(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const [animatePager, setAnimatePager] = useState<
    'left' | 'right' | undefined
  >();

  const isMobile = useBreakpointValue(
    { base: true, tablet: false },
    { fallback: 'tablet' },
  );

  const calculateNewX = () => -index * (containerRef.current?.clientWidth || 0);
  const hasMultipleElements = numberOfElements > 1;

  const shouldAutoPlay = hasMultipleElements && autoPlay;
  const shouldDrag = hasMultipleElements && isMobile;

  const [isPlaying, { on: play, off: pause, toggle }] =
    useBoolean(shouldAutoPlay);

  const onHold = useCallback(() => {
    pause();
  }, [pause]);

  const onRelease = useCallback(() => {
    if (shouldAutoPlay) play();
  }, [play, shouldAutoPlay]);

  const previous = () => {
    if (!loop && index === 0) return;
    setIndex(index - 1);
  };
  const next = () => {
    if (!loop && index === numberOfElements - 1) return;
    setIndex(index + 1);
  };
  const jumpToIndex = (newIndex: number) => {
    setIndex(newIndex);
  };

  const handleEndDrag = (_e: Event, dragProps: PanInfo) => {
    const clientWidth = containerRef.current?.clientWidth || 0;

    const { offset, velocity } = dragProps;

    if (Math.abs(velocity.y) > Math.abs(velocity.x)) {
      animate(x, calculateNewX(), { type: 'spring', bounce: 0 });
      return;
    }

    if (offset.x > clientWidth / 4 && (loop || index > 0)) {
      previous();
    } else if (
      offset.x < -clientWidth / 4 &&
      (loop || index < numberOfElements - 1)
    ) {
      next();
    } else {
      animate(x, calculateNewX(), { type: 'spring', bounce: 0 });
    }
  };

  useEffect(() => {
    const controls = animate(x, calculateNewX(), { type: 'spring', bounce: 0 });
    return controls.stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  const shouldShowArrow = (direction: 'next' | 'previous') => {
    if (isPlaying) return false;
    if (!hasMultipleElements) return false;
    if (loop) return true;
    if (direction === 'previous') return index > 0;
    return index < numberOfElements - 1;
  };

  return (
    <MotionBox
      height="100%"
      onMouseEnter={pause}
      onMouseLeave={play}
      onTouchCancel={onRelease}
      onTouchEnd={onRelease}
      onTouchStart={onHold}
      overflowX="hidden"
      position="relative"
      ref={containerRef}
      width="100%"
      {...rest}
    >
      {range.map(rangeValue => (
        <CarouselElement
          draggable={shouldDrag}
          index={rangeValue + index}
          key={rangeValue + index}
          onDragEnd={handleEndDrag}
          renderPage={
            !hasMultipleElements && rangeValue !== 0 ? () => null : children
          }
          x={x}
        />
      ))}
      <CarouselArrow
        backgroundColor={controlsBackground}
        direction="previous"
        left={800}
        onClick={() => {
          setAnimatePager('left');
        }}
        position="absolute"
        shouldShow={shouldShowArrow('previous')}
        top="50%"
      />
      <CarouselArrow
        backgroundColor={controlsBackground}
        direction="next"
        onClick={() => {
          setAnimatePager('right');
        }}
        position="absolute"
        right={800}
        shouldShow={shouldShowArrow('next')}
        top="50%"
      />
      <Box
        alignItems="center"
        bottom={0}
        display="grid"
        gridTemplateColumns="1fr 2fr 1fr"
        gridTemplateRows="1fr"
        pb={{ base: 600, tablet: 800 }}
        position="absolute"
        width="100%"
      >
        <Center
          backgroundColor={controlsBackground}
          borderRadius={500}
          display={hasMultipleElements ? 'flex' : 'none'}
          gridColumn="2 / 3"
          height={200}
          mb={50}
          mx="auto"
          px={100}
        >
          {loop ? (
            <InfinitePager
              animate={animatePager}
              animateReset={setAnimatePager}
              bottom={{ base: 600, tablet: 700 }}
              isPlaying={isPlaying && shouldAutoPlay}
              next={next}
              onToggle={toggle}
              previous={previous}
            />
          ) : (
            <SlidePager
              activeIndex={index}
              animate={animatePager}
              animateReset={setAnimatePager}
              bottom={{ base: 600, tablet: 700 }}
              isPlaying={isPlaying && shouldAutoPlay}
              maxVisibleDots={5}
              next={next}
              numberOfElements={numberOfElements}
              onDotClick={jumpToIndex}
              onToggle={toggle}
              previous={previous}
            />
          )}
        </Center>
        <Button
          backgroundColor={controlsBackground}
          gridColumn="3 / 4"
          height={350}
          justifySelf="end"
          marginRight={{ base: 500, tablet: 800 }}
          ml="auto"
          onClick={() =>
            setCurrentMenu(HomeownerAppMenuNames.ADD_IDEAS_TO_PROJECT)
          }
          shape="circle"
          variant="primary"
          width={350}
        >
          <Icon icon={iPlus} />
        </Button>
      </Box>
    </MotionBox>
  );
};

export { InfiniteCarousel };
